/**
 * Button Component
 *
 * Botão reutilizável com variantes baseadas no sistema de theme TMF.
 * Usa padrão shadcn/ui: CVA + Radix Slot + cn().
 * Suporta dark mode e CSS variables do design system.
 *
 * @module components/ui
 * @since 2025-12-15
 * @updated 2026-04-06 - Migrado para shadcn/ui (CVA + Radix Slot + cn())
 *
 * @example
 * // Botão primário básico
 * <Button onClick={handleClick}>Salvar</Button>
 *
 * @example
 * // Botão com loading state
 * <Button loading={isSubmitting} disabled={!isValid}>
 *   Enviar
 * </Button>
 *
 * @example
 * // Botão com ícone à esquerda
 * <Button icon={<PlusIcon />} variant="success">
 *   Adicionar
 * </Button>
 *
 * @example
 * // Botão de perigo para ações destrutivas
 * <Button variant="danger" size="sm" onClick={handleDelete}>
 *   Excluir
 * </Button>
 *
 * @example
 * // asChild pattern (shadcn)
 * <Button asChild><a href="/home">Home</a></Button>
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/** Variantes visuais disponíveis para o botão */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'outline'
  | 'ghost';

/** Tamanhos disponíveis para o botão */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export const buttonVariants = cva(
  [
    'tmf-btn',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
    'transition-all duration-150',
    'hover:opacity-90 active:scale-[0.97]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'tmf-btn-primary',
        secondary: 'tmf-btn-secondary',
        success: 'tmf-btn-success',
        danger: 'tmf-btn-danger',
        warning: 'tmf-btn-warning',
        info: 'tmf-btn-info',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        ghost:
          'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        xs: 'h-7 px-2 text-xs rounded',
        sm: 'h-8 px-3 text-sm rounded-lg',
        md: 'h-9 px-4 text-sm rounded-lg',
        lg: 'h-10 px-6 text-base rounded-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

/**
 * Props do componente Button
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'fullWidth'> {
  /** Conteúdo do botão (texto, ícones, etc) */
  children: React.ReactNode;
  /** Variante visual do botão @default 'primary' */
  variant?: ButtonVariant;
  /** Tamanho do botão @default 'md' */
  size?: ButtonSize;
  /** Exibe spinner de loading e desabilita o botão @default false */
  loading?: boolean;
  /** Ícone a ser exibido junto ao texto */
  icon?: React.ReactNode;
  /** Posição do ícone em relação ao texto @default 'left' */
  iconPosition?: 'left' | 'right';
  /** Se true, o botão ocupa 100% da largura do container @default false */
  fullWidth?: boolean;
  /** Se true, renderiza como filho (padrão shadcn asChild) @default false */
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      asChild = false,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({ variant, size, fullWidth }),
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {icon && iconPosition === 'left' && !loading && (
          <span className="shrink-0">{icon}</span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="shrink-0">{icon}</span>
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
